import React from "react";
import {
	Box,
	Button,
	Paper,
	Typography,
	Grid,
	Card,
	CardContent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
} from "@mui/material";
import {
	ArrowBack as ArrowBackIcon,
	Download as DownloadIcon,
} from "@mui/icons-material";
import { saveAs } from "file-saver";
import { downloadFile } from "./services/api";

function Results({ results, onBack }) {
	if (!results) {
		return (
			<Box>
				<Typography>No results available</Typography>
				<Button onClick={onBack} variant="outlined" sx={{ mt: 2 }}>
					Back
				</Button>
			</Box>
		);
	}

	const { summary = {}, output_file } = results;

	const handleDownload = async () => {
		if (!output_file) return;
		try {
			const response = await downloadFile(output_file);
			saveAs(response.data, output_file);
		} catch (error) {
			console.error("Download failed:", error);
		}
	};

	const summaryCards = [
		{ title: "Total Required", value: (summary.total_required ?? 0).toLocaleString(), unit: "units" },
		{ title: "Total Allocated", value: (summary.total_allocated ?? 0).toLocaleString(), unit: "units" },
		{ title: "Allocation Rate", value: `${summary.allocation_percentage ?? 0}%`, unit: "success rate" },
		{ title: "Fully Allocated", value: (summary.fully_allocated ?? 0).toLocaleString(), unit: "records" },
		{ title: "Partial Allocated", value: (summary.partial_allocated ?? 0).toLocaleString(), unit: "records" },
		{ title: "No Stock", value: (summary.no_stock ?? 0).toLocaleString(), unit: "records" },
	];

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Allocation Results
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Allocation process completed successfully. Review the summary below and download the results file.
			</Typography>

			<Grid container spacing={3} sx={{ mb: 3 }}>
				{summaryCards.map((card) => (
					<Grid item xs={12} sm={6} md={4} key={card.title}>
						<Card>
							<CardContent>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									{card.title}
								</Typography>
								<Typography variant="h4" color="primary">
									{card.value}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{card.unit}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>

			{summary.allocation_by_warehouse && Object.keys(summary.allocation_by_warehouse).length > 0 && (
				<Paper variant="outlined" sx={{ mb: 3 }}>
					<Typography variant="h6" sx={{ p: 2, pb: 1 }}>
						Allocation by Warehouse
					</Typography>
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>
										<strong>Warehouse</strong>
									</TableCell>
									<TableCell align="right">
										<strong>Allocated Units</strong>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{Object.entries(summary.allocation_by_warehouse)
									.sort((a, b) => b[1] - a[1])
									.map(([warehouse, units]) => (
										<TableRow key={warehouse}>
											<TableCell>
												<Chip label={warehouse} color="primary" size="small" />
											</TableCell>
											<TableCell align="right">
												<strong>{units.toLocaleString()}</strong>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			)}

			<Box display="flex" justifyContent="space-between" mt={4}>
				<Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined">
					Back
				</Button>
				<Button startIcon={<DownloadIcon />} onClick={handleDownload} variant="contained" size="large" disabled={!output_file}>
					Download Results
				</Button>
			</Box>
		</Box>
	);
}

export default Results;
